import { Suspense, useCallback, useEffect } from 'react';
import { QueryResult } from '../../components/Alerts.tsx';
import { useAppSelector, useAppStore } from '../../lib/hooks';
import { selectBuilding } from '../../lib/model/building/buildingSlice';
import { FirestoreBuildingView, useGetBuildingsQuery } from '../../lib/model/kantoordag/buildingsStreamingApi.ts';
import { useTranslation } from 'react-i18next'
import { MegaMenuDropdown, NavbarLink } from 'flowbite-react';

export default function BuildingSelector() {
    const { t } = useTranslation("header");

    const store = useAppStore();
    const user = useAppSelector(state => state.user.user)
    const selectedBuilding = useAppSelector(state => state.building.building)
    const selectedBuildingName = (selectedBuilding == undefined ? " (...)" : ` (${selectedBuilding.name})`)
    // BuildingSelector renders globally (inside Header), so it mounts before sign-in completes --
    // unlike this app's other Firestore-streamed queries, which only mount on already-authenticated
    // routes. Firestore's onSnapshot treats permission-denied as terminal (unlike a transient
    // network error, it does not auto-retry), and createFirestoreStreamApi opens exactly one
    // listener per query -- so subscribing before request.auth is set would permanently strand this
    // query in an error state with no later retry, even after sign-in succeeds a moment later.
    // Skipping until `user` is set defers the subscription past that race, matching the same
    // pattern the other streaming APIs already use (e.g. kantoordagMyReservationsStreamingApi's
    // `skip: !user?.email`).
    const gebouwQuery = useGetBuildingsQuery(undefined, { skip: user == null })

    const doSelectBuilding = useCallback((gebouw: FirestoreBuildingView) => {
        store.dispatch(selectBuilding(gebouw))
    }, [store])

    useEffect(() => {
        if (gebouwQuery.isSuccess) {
            const defaultBuilding = gebouwQuery.data.find(building => building.default)
            if (defaultBuilding) {
                doSelectBuilding(defaultBuilding)
            }
        }

    }, [gebouwQuery, doSelectBuilding]);

    return (
        <NavbarLink>
            <MegaMenuDropdown toggle={<>{t("building") + selectedBuildingName}</>}>
                <Suspense>
                    <QueryResult query={gebouwQuery} message={t("loading_failed")}>
                        {
                            gebouwQuery.data !== undefined ?
                                (
                                    <BuildingList buildings={gebouwQuery.data} onSelectBuilding={doSelectBuilding}/>
                                ) :  null
                        }
                    </QueryResult>
                </Suspense>
            </MegaMenuDropdown>
        </NavbarLink>
    )
}

function BuildingList(
    {
      buildings,
      onSelectBuilding
    } : {
        buildings: Array<FirestoreBuildingView>,
        onSelectBuilding: (building: FirestoreBuildingView) => void
    }) {
    const selectedBuilding = useAppSelector(state => state.building.building)

    return (
        <div className={"cursor-pointer"}>
            {buildings.map((building, index) =>
                (
                    <div onClick={() => onSelectBuilding(building)} key={building.id}
                         className={index == buildings.length -  1 ? "" : "border-b"}>
                        <div className="items-center space-x-1 grid grid-cols-2">
                            <img src={building.picture} className="max-w-[128px]" alt={building.name}/>

                            <div className={building.id == selectedBuilding?.id ? "text-red-600" : "text-gray-900 dark:text-white"}>
                                <h5 className="text-l font-bold tracking-tight mr-2" >
                                    {building.name}
                                </h5>
                            </div>
                        </div>
                    </div>
                )
            )}
        </div>
    )
}

